import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import {
  ArrowRight,
  Camera,
  DollarSign,
  FileText,
  Handshake,
  TrendingUp,
  Video,
} from "lucide-react";

const sellingSteps = [
  {
    number: "01",
    title: "Honest Pricing",
    description:
      "I'll tell you exactly what your home is worth — not what you want to hear. Overpricing kills deals. We price it right from day one.",
    icon: DollarSign,
  },
  {
    number: "02",
    title: "Prep & Stage",
    description:
      "Small investments, big returns. I'll tell you exactly what to fix (and what not to waste money on) to maximize your sale price.",
    icon: FileText,
  },
  {
    number: "03",
    title: "Professional Marketing",
    description:
      "Pro photos, video tours, social media blitz, and targeted advertising. Your home deserves to look its best.",
    icon: Camera,
  },
  {
    number: "04",
    title: "Show & Negotiate",
    description:
      "I handle the showings, screen the buyers, and negotiate hard. My job is to get you top dollar with the best terms.",
    icon: Handshake,
  },
  {
    number: "05",
    title: "Close The Deal",
    description:
      "Inspections, appraisals, paperwork — I handle it all. You just pack your bags and collect your check.",
    icon: TrendingUp,
  },
];

const marketingPlan = [
  {
    icon: Camera,
    title: "Professional Photography",
    description: "HDR photos that make your home look its absolute best",
  },
  {
    icon: Video,
    title: "Video Tours",
    description: "Cinematic walkthrough videos for online buyers",
  },
  {
    icon: TrendingUp,
    title: "Social Media Blitz",
    description: "Targeted ads on Instagram, Facebook, and more",
  },
  {
    icon: FileText,
    title: "MLS & Syndication",
    description: "Listed everywhere: Zillow, Redfin, Realtor.com, and more",
  },
];

export default function SellPage() {
  return (
    <div className="page-transition" data-testid="sell-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl space-y-8">
            <div className="space-y-4">
              <div className="gold-line" />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white">
                Sell With <span className="text-[#D4AF37]">Me</span>
              </h1>
            </div>
            <p className="text-xl text-zinc-300 leading-relaxed">
              I'll tell you exactly what your home is worth — not what you want
              to hear. Then I'll get you every dollar it deserves.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest px-8 py-6"
                  data-testid="sell-hero-contact-button"
                >
                  Get Your Home Value
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:+19092335411">
                <Button
                  className="bg-transparent border border-white text-white hover:bg-white hover:text-black rounded-none font-bold uppercase tracking-widest px-8 py-6"
                  data-testid="sell-hero-call-button"
                >
                  Let's Talk
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <blockquote className="text-2xl md:text-3xl font-bold text-white leading-tight">
            "I'll tell you exactly what your home is worth —{" "}
            <span className="text-[#D4AF37]">not what you want to hear.</span>"
          </blockquote>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
              The <span className="text-[#D4AF37]">Selling</span> Process
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              From listing to closing, here's exactly how we'll sell your home
              for top dollar.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/20" />

            <div className="space-y-12 md:space-y-24">
              {sellingSteps.map((step, index) => (
                <div
                  key={step.number}
                  className={`relative flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  data-testid={`selling-step-${step.number}`}
                >
                  {/* Content */}
                  <div
                    className={`flex-1 ${
                      index % 2 === 0 ? "md:text-right" : "md:text-left"
                    }`}
                  >
                    <div
                      className={`card-dark p-8 space-y-4 ${
                        index % 2 === 0 ? "md:ml-auto md:mr-12" : "md:mr-auto md:ml-12"
                      } max-w-lg`}
                    >
                      <span className="stroke-text text-6xl font-black">
                        {step.number}
                      </span>
                      <h3 className="text-2xl font-bold text-white">
                        {step.title}
                      </h3>
                      <p className="text-zinc-400 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Center icon */}
                  <div className="hidden md:flex w-16 h-16 bg-[#D4AF37] items-center justify-center z-10">
                    <step.icon className="w-8 h-8 text-black" />
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Plan */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
              Marketing <span className="text-[#D4AF37]">Plan</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Your home deserves serious marketing. Here's what I bring:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {marketingPlan.map((item) => (
              <div
                key={item.title}
                className="card-dark p-8 text-center space-y-4"
                data-testid={`marketing-item-${item.title}`}
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center mx-auto">
                  <item.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-bold text-white">{item.title}</h3>
                <p className="text-zinc-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="gold-line" />
                <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
                  Ready To <span className="text-[#D4AF37]">Sell?</span>
                </h2>
              </div>
              <p className="text-zinc-400 leading-relaxed">
                Get my Seller Prep Guide — no fluff, just the facts on what you
                need to do to sell for top dollar. Plus, let's schedule a time
                to talk about your home's value.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#D4AF37]" />
                  <span className="text-zinc-300">Realistic pricing strategy</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#D4AF37]" />
                  <span className="text-zinc-300">Prep checklist that actually matters</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#D4AF37]" />
                  <span className="text-zinc-300">Timeline from listing to closing</span>
                </div>
              </div>
            </div>

            <div className="card-dark p-8 md:p-12 border-[#D4AF37]/30">
              <LeadCaptureForm
                type="seller"
                title="Get Your Seller Prep Guide"
                subtitle="No fluff, just facts on selling for top dollar."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
